"use client";

import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Revenue() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tiers = [{
    name: "Essential",
    value: "Foundational revenue strategies to stabilize occupancy and streamline daily operations.",
    features: ["Dynamic rate monitoring", "Channel manager audit", "Competitor price tracking", "Basic performance reporting"],
    borderGradient: "linear-gradient(140deg, hsl(43 46% 55%), hsl(43 46% 70%))",
    featured: false
  }, {
    name: "Advanced",
    value: "Data-driven optimization to maximize RevPAR and capture high-value market segments.",
    features: ["Predictive demand modeling", "Automated yield management", "OTA listing optimization", "Advanced market intelligence"],
    borderGradient: "linear-gradient(140deg, hsl(222 47% 7%), hsl(222 47% 20%))",
    featured: true
  }, {
    name: "Enterprise",
    value: "Comprehensive revenue ecosystem management for high-performance hotel portfolios.",
    features: ["Custom revenue strategy", "Portfolio-wide analytics", "Direct booking conversion", "Dedicated revenue consultant"],
    borderGradient: "linear-gradient(140deg, hsl(215 16% 47%), hsl(215 16% 88%))",
    featured: false
  }];
  return <section data-section-id="13734" id="hotel-revenue-management-consulting" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32">
      {}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-60" style={{
      background: "radial-gradient(circle at 15% 20%, hsl(43 46% 55% / 0.15), transparent 45%), radial-gradient(circle at 85% 15%, hsl(222 47% 7% / 0.1), transparent 50%), radial-gradient(circle at 70% 80%, hsl(215 16% 47% / 0.1), transparent 55%), radial-gradient(circle at 20% 90%, hsl(43 46% 55% / 0.1), transparent 50%)"
    }} />

      <div ref={ref} className="relative mx-auto w-full max-w-[1280px] px-5 sm:px-8 lg:px-14">
        {}
        <div className="mb-16 grid grid-cols-1 gap-8 lg:mb-[72px] lg:grid-cols-12">
          <div className={`lg:col-span-7 transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <h2 className="font-default text-foreground" style={{
            fontSize: "clamp(2rem, 4vw, 3.5rem)",
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1.05
          }}>
              Revenue strategies{" "}
              <span className="italic" style={{
              color: "hsl(43 46% 45%)",
              fontWeight: 400
            }}>
                for your property
              </span>
            </h2>
            <p className="mt-6 max-w-xl text-foreground/75" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.1875rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em"
          }}>
              Three specialized consulting tiers designed to increase your hotel's profitability. 
              We leverage industry-leading data and proven revenue management methodologies 
              to ensure your property outperforms the local market.
            </p>
          </div>
          <div className="hidden lg:col-span-5 lg:block" aria-hidden />
        </div>

        {}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {tiers.map((tier, i) => <div key={tier.name} data-index={i} className={`group relative rounded-lg p-[1px] transition-all duration-700 ease-out hover:-translate-y-1 ${tier.featured ? "md:-mt-2" : ""} ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
          background: tier.borderGradient,
          transitionDelay: `${i * 80}ms`,
          boxShadow: "0 4px 12px 0px hsl(222 47% 7% / 0.1)"
        }}>
              <div className="relative flex h-full min-h-[460px] flex-col rounded-lg bg-card p-9 backdrop-blur-sm">
                {tier.featured && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 border-secondary/25 bg-secondary/10 text-secondary-foreground" style={{
              borderWidth: "1px",
              borderStyle: "solid"
            }}>
                    Recommended
                  </Badge>}

                <h3 className="font-default text-foreground" style={{
              fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)",
              fontWeight: 600,
              letterSpacing: "-0.018em",
              lineHeight: 1.15
            }}>
                  {tier.name}
                </h3>

                <p className="mt-3 text-foreground/80" style={{
              fontSize: "clamp(1.0625rem, 1.25vw, 1.1875rem)",
              lineHeight: 1.55
            }}>
                  {tier.value}
                </p>

                <ul className="mt-8 flex flex-col gap-3">
                  {tier.features.map((feature, j) => <li key={feature} data-index={j} className="flex items-start gap-3 text-foreground" style={{
                fontSize: "1rem",
                lineHeight: 1.5
              }}>
                      <Check className="mt-1 shrink-0 text-secondary" style={{
                  width: "14px",
                  height: "14px"
                }} strokeWidth={2.5} />
                      <span>{feature}</span>
                    </li>)}
                </ul>

                <div className="mt-10 pt-2">
                  <Button asChild variant="link" className="h-auto p-0 text-foreground hover:no-underline">
                    <Link to="/ContactPage" className="group/link relative inline-flex items-center">
                      <span className="relative pb-1" style={{
                    fontSize: "1rem",
                    fontWeight: 500
                  }}>
                        Get started
                        <span className="absolute left-0 right-0 bottom-0 h-[1px] bg-secondary transition-all duration-300 ease-out" style={{
                      transform: "translateY(2px)"
                    }} aria-hidden />
                      </span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}